import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { readModelArkAssetStatus, registerModelArkAssets, recheckModelArkAssets } from '../services/modelArkAssetService';
import { isModelArkSeedance25Provider } from '../utils/seedance25Capabilities';
import { canRegisterModelArkAsset, canRecheckModelArkAsset, getModelArkLibraryProvider, modelArkReferenceKey, normalizeModelArkReferences, usesModelArkAssetLibrary } from '../utils/modelArkAssets';

// Registry state belongs to an original URL + media type + target library, never
// an asset object or variation array index. No workshop entity is written here.
export function useModelArkAssets({ projectId, projectProvider, references = [], enabled = true, refreshIntervalMs = 0, refreshOnFocus = false }) {
  const projectLibraryProvider = getModelArkLibraryProvider(projectProvider);
  const selectionScope = `${projectId || 'global'}:${projectProvider || ''}`;
  const [selectionState, setSelectionState] = useState({ scope: selectionScope, value: 'project' });
  // Derive the reset during render, not in an effect: a model switch must not
  // briefly keep the previous model's manually selected target library.
  const selection = usesModelArkAssetLibrary(projectProvider)
    ? 'project'
    : selectionState.scope === selectionScope ? selectionState.value : 'project';
  const setSelection = useCallback((next) => setSelectionState((previous) => ({
    scope: selectionScope,
    value: usesModelArkAssetLibrary(projectProvider)
      ? 'project'
      : typeof next === 'function' ? next(previous.scope === selectionScope ? previous.value : 'project') : next,
  })), [projectProvider, selectionScope]);
  useEffect(() => {
    setSelectionState((previous) => previous.scope === selectionScope
      ? previous : { scope: selectionScope, value: 'project' });
  }, [selectionScope]);
  const provider = selection === 'project' ? projectLibraryProvider : selection;
  const [snapshot, setSnapshot] = useState({ scope: '', items: [], providers: [] });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [readErrorState, setReadErrorState] = useState({ scope: '', message: '' });
  const [actionErrorState, setActionErrorState] = useState({ scope: '', message: '' });
  const [refreshSequence, setRefreshSequence] = useState(0);
  const requestEpoch = useRef(0);
  const registrationInFlight = useRef(false);
  const statusReadInFlight = useRef(false);
  const completedScopeRef = useRef('');
  const completedSnapshotRef = useRef(null);
  const signature = JSON.stringify(normalizeModelArkReferences(references).sort((a, b) => modelArkReferenceKey(a).localeCompare(modelArkReferenceKey(b))));
  const normalized = useMemo(() => JSON.parse(signature), [signature]);
  const currentScope = `${selectionScope}:${provider}:${signature}`;
  const currentContextRef = useRef(null);
  currentContextRef.current = currentScope;
  // Current official 2.0 and both active 2.5 routes use their model's library.
  // A model switch changes the scope and immediately hides the previous snapshot.
  const active = enabled && usesModelArkAssetLibrary(projectProvider);
  const interval = Number.isFinite(Number(refreshIntervalMs)) && Number(refreshIntervalMs) > 0
    ? Math.max(1000, Number(refreshIntervalMs)) : 0;
  const setError = (message) => setActionErrorState({ scope: currentScope, message });
  const readError = readErrorState.scope === currentScope && active ? readErrorState.message : '';
  const actionError = actionErrorState.scope === currentScope && active ? actionErrorState.message : '';
  const error = [readError, actionError].filter(Boolean).join('；');
  const refreshStatus = useCallback(() => {
    completedScopeRef.current = '';
    completedSnapshotRef.current = null;
    setRefreshSequence((value) => value + 1);
  }, []);
  const refresh = useCallback(() => {
    // An explicit refresh acknowledges the preceding action failure. Background
    // reads and the post-submission refresh must preserve that diagnostic.
    setActionErrorState({ scope: currentContextRef.current, message: '' });
    refreshStatus();
  }, [refreshStatus]);
  useEffect(() => {
    const epoch = ++requestEpoch.current;
    const abort = new AbortController();
    let timer;
    completedScopeRef.current = '';
    completedSnapshotRef.current = null;
    statusReadInFlight.current = false;
    if (!active) { setLoading(false); return () => abort.abort(); }
    const isCurrent = () => !abort.signal.aborted && epoch === requestEpoch.current && currentContextRef.current === currentScope;
    const schedule = (delay) => {
      clearTimeout(timer);
      if (delay > 0 && isCurrent()) timer = setTimeout(poll, delay);
    };
    const poll = async () => {
      if (!isCurrent() || statusReadInFlight.current) return;
      clearTimeout(timer);
      statusReadInFlight.current = true;
      completedScopeRef.current = '';
      completedSnapshotRef.current = null;
      setLoading(true);
      try {
        const items = [];
        let providers = [];
        // Also request an empty page so configuration warnings remain visible.
        for (let offset = 0; offset < Math.max(1, normalized.length); offset += 100) {
          const result = await readModelArkAssetStatus({ projectId: projectId || undefined, references: normalized.slice(offset, offset + 100) }, abort.signal);
          if (!isCurrent()) return;
          items.push(...(result?.items || []));
          providers = result?.providers || providers;
        }
        if (!isCurrent()) return;
        const nextSnapshot = { scope: currentScope, items, providers };
        setSnapshot(nextSnapshot);
        completedScopeRef.current = currentScope;
        completedSnapshotRef.current = nextSnapshot;
        setReadErrorState({ scope: currentScope, message: '' });
        schedule(items.some((item) => ['Pending', 'Processing'].includes(item.status)) ? 4000 : interval);
      } catch (failure) {
        if (isCurrent()) {
          completedScopeRef.current = '';
          completedSnapshotRef.current = null;
          setSnapshot({ scope: currentScope, items: [], providers: [] });
          setReadErrorState({ scope: currentScope, message: failure.message || '入库状态读取失败' });
          // Periodic recovery is read-only; never register or recreate on a failure.
          schedule(interval);
        }
      } finally {
        if (isCurrent()) { statusReadInFlight.current = false; setLoading(false); }
      }
    };
    const onFocus = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') void poll();
    };
    if (refreshOnFocus && typeof window !== 'undefined') window.addEventListener('focus', onFocus);
    if (refreshOnFocus && typeof document !== 'undefined') document.addEventListener('visibilitychange', onFocus);
    poll();
    return () => {
      abort.abort(); clearTimeout(timer);
      if (refreshOnFocus && typeof window !== 'undefined') window.removeEventListener('focus', onFocus);
      if (refreshOnFocus && typeof document !== 'undefined') document.removeEventListener('visibilitychange', onFocus);
    };
  }, [active, projectId, normalized, currentScope, refreshSequence, interval, refreshOnFocus]);
  const currentSnapshot = active && snapshot.scope === currentScope;
  const items = useMemo(() => new Map((currentSnapshot ? snapshot.items : []).map((item) => [modelArkReferenceKey(item, item.provider), item])), [snapshot.items, currentSnapshot]);
  const getStatus = (reference, target = provider) => items.get(modelArkReferenceKey(reference, target));
  const targetConfiguration = currentSnapshot ? snapshot.providers.find((entry) => entry.provider === provider) : undefined;
  const canUseSnapshot = () => active && currentContextRef.current === currentScope
    && completedScopeRef.current === currentScope && completedSnapshotRef.current === snapshot
    && !statusReadInFlight.current && currentSnapshot;
  const register = async (referencesToRegister = normalized, { retryFailed = false } = {}) => {
    if (registrationInFlight.current) return;
    setError('');
    if (!canUseSnapshot()) { setError('请等待当前库入库状态读取完成后再提交'); return; }
    if (!isModelArkSeedance25Provider(provider)) { setError('请先选择国内 Seedance 或海外 Dreamina 目标库'); return; }
    if (targetConfiguration?.configured !== true) { setError(targetConfiguration?.error || '当前目标库未配置或配置未读取，请刷新或联系管理员'); return; }
    const candidates = normalizeModelArkReferences(referencesToRegister).filter((item) => canRegisterModelArkAsset(getStatus(item)?.status, retryFailed));
    if (!candidates.length) return;
    const requestProvider = provider;
    const requestProject = projectId;
    const requestContext = currentContextRef.current;
    registrationInFlight.current = true;
    setSubmitting(true);
    try {
      for (let offset = 0; offset < candidates.length; offset += 100) {
        if (currentContextRef.current !== requestContext) break;
        await registerModelArkAssets({ projectId: requestProject || undefined, provider: requestProvider, references: candidates.slice(offset, offset + 100), retryFailed });
      }
    } catch (failure) {
      if (currentContextRef.current === requestContext) setError(failure.message || '入库提交失败，请刷新核查状态后再操作');
    } finally {
      registrationInFlight.current = false;
      setSubmitting(false);
      refreshStatus();
    }
  };
  const recheck = async (referencesToRecheck = normalized) => {
    if (registrationInFlight.current) return;
    setError('');
    if (!canUseSnapshot()) { setError('请等待当前库入库状态读取完成后再查询'); return; }
    if (!isModelArkSeedance25Provider(provider)) { setError('请先选择国内 Seedance 或海外 Dreamina 目标库'); return; }
    if (targetConfiguration?.configured !== true) { setError(targetConfiguration?.error || '当前目标库未配置或配置未读取，请刷新或联系管理员'); return; }
    const candidates = normalizeModelArkReferences(referencesToRecheck).filter((item) => canRecheckModelArkAsset(getStatus(item)));
    if (!candidates.length) return;
    const requestProvider = provider;
    const requestProject = projectId;
    const requestContext = currentContextRef.current;
    registrationInFlight.current = true;
    setSubmitting(true);
    try {
      for (let offset = 0; offset < candidates.length; offset += 100) {
        if (currentContextRef.current !== requestContext) break;
        // The server re-queries a previously saved remote ID. Never send this
        // operation through register/create, even after a timeout or failure.
        await recheckModelArkAssets({ projectId: requestProject || undefined, provider: requestProvider, references: candidates.slice(offset, offset + 100) });
      }
    } catch (failure) {
      if (currentContextRef.current === requestContext) setError(failure.message || '重新查询提交失败，请刷新状态或联系管理员核查');
    } finally {
      registrationInFlight.current = false;
      setSubmitting(false);
      refreshStatus();
    }
  };
  return { active, provider, projectProvider: projectLibraryProvider, selection, setSelection, getStatus, loading: active && (!currentSnapshot || loading), submitting, error, readError, actionError, refresh, register, recheck, references: normalized, targetConfiguration };
}
